import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { CloseButton } from '@repo/common';
import { ReactComponent as ChevronIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as MoreIcon } from '@repo/common/assets/icons/16px/icon_16px_more-vert.svg';
import {
  CHECKIN_STATUS,
  JOB_LABEL,
  checkinDeadline,
  checkinOpensAt,
  checkinStatus,
  checkpointSummary,
  cn,
  isBackFromOffline,
  isCheckinOpen,
  isDueNow,
  isLeader,
  isTargetOf,
  markCheckpointsSeen,
} from '../../components/TwoDays/model/derive';
import { formatClock, formatDue, tzShortName } from '../../components/TwoDays/model/time';
import { useWorkboardCtx } from '../../components/TwoDays/model/useWorkboard';
import { MemberAvatar, StatusChip } from '../../components/TwoDays/primitives';
import {
  CheckInForm,
  GeneralCheckpointModal,
  jobsText,
} from '../../components/TwoDays/CheckpointModals';
import { Modal, ModalButton } from '../../components/TwoDays/Modal';
import TdButton from '../../components/TwoDays/Button';
import SidePanel from '../../components/TwoDays/SidePanel';
import useInboxFocus, { litClass } from '../../components/TwoDays/useInboxFocus';
import Linkify from '../../components/TwoDays/Linkify';

const byTime = (a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt);
const card = 'rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00';
const LIT_CARD =
  'shadow-[0_0_0_1px_#1dc37b,0_2px_4px_0_rgba(121,111,98,0.06),0_4px_12px_0_rgba(121,111,98,0.12)]';

/** 남은 시간 — "3h 40m" · "25 min" */
const untilLabel = (target, now) => {
  const mins = Math.max(0, Math.ceil((new Date(target) - now) / 60000));
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

/** 늦은 정도 — 체크인 시각 − 체크인 마감 */
const lateMs = (cp, member, ci) => new Date(ci.checkedInAt).getTime() - checkinDeadline(cp, member);
const lateLabel = (mins) =>
  mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ''}`;

/** 대상 직군 — requested 는 팀 전체 */
const forLabel = (cp) =>
  cp.type === 'requested' ? 'Everyone' : cp.targetJobs.map((j) => JOB_LABEL[j]).join(', ');

/* ── My check-ins ────────────────────────────────────────── */

/** 상단 카드 — Due now 면 체크인 폼을 펼치고, 아니면 다음 체크포인트까지 남은 시간 */
const TopCard = ({ cp, dueNow, lit }) => {
  const { now, displayTz, viewer, closed } = useWorkboardCtx();
  const tz = tzShortName(displayTz, now);
  const deadline = checkinDeadline(cp, viewer);
  // 체크인은 체크포인트 30분 전부터 — 그 전에는 버튼이 잠기고 열리는 시각을 보여 준다.
  // 열린 뒤 버튼을 누르면 카드 안에 폼이 펼쳐진다. Due now 면 처음부터 펼쳐 둔다
  const [early, setEarly] = useState(false);
  const open = isCheckinOpen(cp, now);
  // 프로젝트를 마쳤으면 체크인하지 않는다
  const formOpen = !closed && (dueNow || (early && open));
  const opensLabel = formatClock(new Date(checkinOpensAt(cp)), displayTz);
  useEffect(() => {
    if (lit) setEarly(true);
  }, [lit]);

  return (
    // Due now — 빨간 테두리 · 연한 빨간 배경 · 빨간 점 (시안 W6_MyCheckins(Due now))
    <section
      data-inbox-lit={lit || undefined}
      className={cn(
        card,
        'flex flex-col gap-5 px-5 py-5 transition-[background-color,border-color,box-shadow] duration-300',
        dueNow && 'border-functional-failed03 bg-functional-failed01/40',
        lit && LIT_CARD,
      )}
    >
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex min-w-0 flex-col gap-0.5'>
          <span
            className={cn(
              'flex items-center gap-1.5 text-[13px]',
              dueNow ? 'font-medium text-functional-failed04' : 'text-text-secondary',
            )}
          >
            {dueNow && (
              <span aria-hidden className='h-1.5 w-1.5 rounded-full bg-functional-failed03' />
            )}
            {dueNow ? 'Due now' : `Next · in ${untilLabel(cp.scheduledAt, now)}`}
          </span>
          <span className='text-[18px] font-medium leading-[140%] text-text-primary'>
            {cp.name}
          </span>
          <span className='text-[13px] text-text-secondary'>
            {/* 오프라인이라 마감이 미뤄졌으면 언제까지인지, 아니면 시각과 남은 시간 */}
            {dueNow && deadline > new Date(cp.scheduledAt).getTime()
              ? `You were offline at ${formatClock(new Date(cp.scheduledAt), displayTz)}. Check in by ${formatClock(new Date(deadline), displayTz)} ${tz} to count as on time.`
              : `${formatDue(cp.scheduledAt, displayTz)} ${tz}${
                  new Date(cp.scheduledAt) > now ? ` · in ${untilLabel(cp.scheduledAt, now)}` : ''
                }`}
          </span>
        </div>
        {!formOpen && !closed && (
          <TdButton
            variant='soft'
            disabled={!open}
            title={open ? undefined : `Check-in opens at ${opensLabel} ${tz}`}
            onClick={() => setEarly(true)}
            className='shrink-0'
          >
            {open ? 'Check in' : `Opens ${opensLabel}`}
          </TdButton>
        )}
      </div>
      {formOpen && (
        <CheckInForm
          key={cp.id}
          checkpoint={cp}
          onCancel={dueNow ? undefined : () => setEarly(false)}
        />
      )}
    </section>
  );
};

/** 내 체크포인트 한 줄 */
const MyRow = ({ cp, past, lit }) => {
  const { data, now, displayTz, viewer, closed } = useWorkboardCtx();
  // Edit check-in · Check in late 를 누르면 이 행 안에 폼이 펼쳐진다 (모달 아님)
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (lit && isCheckinOpen(cp, now)) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lit]);
  const litRow = cn(
    'transition-[background-color,box-shadow] duration-300',
    litClass(lit),
  );
  const tz = tzShortName(displayTz, now);
  const ci = (data.checkins ?? []).find((c) => c.checkpointId === cp.id && c.userId === viewer.id);
  const status = checkinStatus(cp, viewer, data.checkins ?? [], now);

  const lateBy = status === 'late' ? Math.round(lateMs(cp, viewer, ci) / 60000) : 0;

  const onCheckIn = () => setOpen(true);
  const info = (
    <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
      <span className='text-[14px] font-medium text-text-primary'>{cp.name}</span>
      <span className='text-[12px] text-text-secondary'>
        {formatDue(cp.scheduledAt, displayTz)} {tz}
      </span>
      {past && (
        <span className='text-[12px] text-text-primary'>
          {ci
            ? `Checked in ${formatDue(ci.checkedInAt, displayTz)} ${tz}${
                lateBy > 0 ? ` · ${lateLabel(lateBy)} late` : ''
              }`
            : 'No check-in. You can still check in late.'}
        </span>
      )}
    </div>
  );

  if (open && !closed) {
    return (
      <li data-inbox-lit={lit || undefined} className={cn('flex flex-col gap-3 px-4 py-3.5', litRow)}>
        <div className='flex items-start gap-3'>
          {info}
          <StatusChip chip={CHECKIN_STATUS[status]} />
        </div>
        <CheckInForm
          checkpoint={cp}
          onCancel={() => setOpen(false)}
          onDone={() => setOpen(false)}
        />
      </li>
    );
  }

  // 지난 항목: 모바일에서는 상태·버튼을 글 아래 줄로 내린다 (시안)
  return (
    <li
      data-inbox-lit={lit || undefined}
      className={cn(
        'flex gap-x-4 gap-y-3 px-4 py-3.5',
        past ? 'flex-col sm:flex-row sm:items-center' : 'items-center',
        litRow,
      )}
    >
      {info}
      <div className='flex flex-wrap items-center gap-2.5'>
        {status === 'on_time' && isBackFromOffline(cp, ci) && (
          <span className='text-[12px] text-text-secondary'>Back from offline</span>
        )}
        <StatusChip chip={CHECKIN_STATUS[status]} />
        {closed ? null : ci ? (
          <TdButton
            variant='ghost'
            onClick={onCheckIn}
            className={cn('shrink-0 px-3', past && '-ml-1 sm:ml-0')}
          >
            Edit check-in
          </TdButton>
        ) : (
          past && (
            <TdButton variant='soft' onClick={onCheckIn} className='shrink-0'>
              Check in late
            </TdButton>
          )
        )}
      </div>
    </li>
  );
};

/**
 * My check-ins — 나에게 온 체크포인트.
 *  · 맨 위 카드: 지금 할 것(Due now) 또는 다음 것
 *  · 아래: 남은 체크포인트 시간순
 *  · 지난 것(체크인 마감이 지남)은 "Earlier · N" 으로 접는다. 놓친 게 있으면 펼친 채로 시작
 */
const MyCheckins = ({ litId = null }) => {
  const { data, now, viewer } = useWorkboardCtx();
  const checkins = data.checkins ?? [];
  const mine = (data.checkpoints ?? []).filter((c) => isTargetOf(c, viewer)).sort(byTime);
  const isPast = (cp) => checkinDeadline(cp, viewer) < now.getTime();
  const checked = (cp) => checkins.some((c) => c.checkpointId === cp.id && c.userId === viewer.id);

  const earlier = mine.filter(isPast).reverse();
  const ahead = mine.filter((cp) => !isPast(cp));
  const top = ahead.find((cp) => !checked(cp));
  const rest = ahead.filter((cp) => cp !== top);
  const [showEarlier, setShowEarlier] = useState(() =>
    earlier.some((cp) => checkinStatus(cp, viewer, checkins, now) === 'missed'),
  );
  useEffect(() => {
    if (litId && earlier.some((cp) => cp.id === litId)) setShowEarlier(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [litId]);

  // 스태프는 체크포인트 대상이 아니다 (평가 제외) — 보기만
  if (viewer?.staff) {
    return (
      <p className='rounded-xl border border-dashed border-grayscale-gray03 px-4 py-10 text-center text-[14px] text-text-secondary'>
        Staff don’t check in. You can follow the team’s check-ins in the Team tab.
      </p>
    );
  }

  if (!viewer || mine.length === 0) {
    return (
      <p className='rounded-xl border border-dashed border-grayscale-gray03 px-4 py-10 text-center text-[14px] text-text-secondary'>
        No checkpoints for you.
      </p>
    );
  }

  return (
    <div className='flex flex-col gap-6'>
      {top ? (
        <TopCard
          cp={top}
          dueNow={isDueNow(top, viewer, checkins, now)}
          lit={!!litId && top.id === litId}
        />
      ) : (
        <p className={cn(card, 'px-5 py-5 text-[14px] text-text-secondary')}>
          You’re all checked in. Nothing else is coming up.
        </p>
      )}

      {rest.length > 0 && (
        <ul className={cn(card, 'divide-y divide-green-deep03/[0.08]')}>
          {rest.map((cp) => (
            <MyRow key={cp.id} cp={cp} lit={cp.id === litId} />
          ))}
        </ul>
      )}

      {earlier.length > 0 && (
        <section className='flex flex-col gap-3'>
          <button
            type='button'
            onClick={() => setShowEarlier((v) => !v)}
            aria-expanded={showEarlier}
            className='flex w-fit items-center gap-1.5 text-[14px] text-text-secondary hover:text-text-primary'
          >
            <ChevronIcon
              className={cn('h-4 w-4 transition-transform', !showEarlier && '-rotate-90')}
            />
            Earlier · {earlier.length}
          </button>
          {showEarlier && (
            <ul className={cn(card, 'divide-y divide-green-deep03/[0.08]')}>
              {earlier.map((cp) => (
                <MyRow key={cp.id} cp={cp} past lit={cp.id === litId} />
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
};

/* ── Team ────────────────────────────────────────────────── */

/**
 * General 행 ⋯ 메뉴 — Edit · Delete(체크인이 있으면 비활성).
 * 표가 가로 스크롤 상자 안에 있어 잘리지 않도록 메뉴는 body 에 띄운다.
 */
const RowMenu = ({ cp, checkinCount, onEdit, onDelete }) => {
  const hasCheckins = checkinCount > 0;
  const [pos, setPos] = useState(null);
  const open = !!pos;
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) =>
      !menuRef.current?.contains(e.target) && !btnRef.current?.contains(e.target) && setPos(null);
    const dismiss = () => setPos(null);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [open]);
  const toggle = () => {
    if (open) return setPos(null);
    const r = btnRef.current.getBoundingClientRect();
    return setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
  };
  const setOpen = (v) => !v && setPos(null);
  const item = 'flex h-9 w-full items-center rounded-md px-3 text-left text-[14px]';

  return (
    <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type='button'
        aria-label={`More for ${cp.name}`}
        aria-expanded={open}
        onClick={toggle}
        className='flex h-8 w-8 items-center justify-center rounded-lg text-icon-secondary hover:bg-grayscale-gray01'
      >
        <MoreIcon className='h-4 w-4 rotate-90' />
      </button>
      {open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            style={{ top: pos.top, right: pos.right }}
            className='fixed z-[1100] flex w-[180px] flex-col rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00 p-1 shadow-4'
          >
            <button
              type='button'
              onClick={() => {
                setOpen(false);
                onEdit();
              }}
              className={cn(item, 'text-text-primary hover:bg-grayscale-gray01')}
            >
              Edit checkpoint
            </button>
            <span
              title={
                hasCheckins
                  ? `Can’t delete · ${checkinCount} check-in${checkinCount === 1 ? '' : 's'}`
                  : undefined
              }
            >
              <button
                type='button'
                disabled={hasCheckins}
                onClick={() => {
                  setOpen(false);
                  onDelete();
                }}
                className={cn(
                  item,
                  hasCheckins
                    ? 'cursor-not-allowed text-text-placeholder'
                    : 'text-functional-failed04 hover:bg-functional-failed01',
                )}
              >
                Delete checkpoint
              </button>
            </span>
          </div>,
          document.body,
        )}
    </div>
  );
};

const COLS =
  'grid grid-cols-[minmax(160px,1.4fr)_130px_minmax(120px,1fr)_minmax(150px,1fr)_120px_40px] items-center gap-4';

/** Requested · General 표. 행을 누르면 팀원별 체크인 Sheet */
const TeamTable = ({ title, desc, action, rows, canManage, onOpen, onEdit, onDelete }) => {
  const { data, now, displayTz, memberById } = useWorkboardCtx();
  const tz = tzShortName(displayTz, now);
  const members = [...memberById.values()];
  const checkins = data.checkins ?? [];

  return (
    <section className='flex flex-col gap-3'>
      <div className='flex flex-wrap items-end justify-between gap-3'>
        <div className='flex flex-col gap-0.5'>
          <h2 className='text-[16px] font-medium text-text-primary'>{title}</h2>
          <span className='text-[13px] text-text-secondary'>{desc}</span>
        </div>
        {action}
      </div>
      {rows.length === 0 ? (
        <p className='rounded-xl border border-dashed border-grayscale-gray03 px-4 py-6 text-center text-[13px] text-text-placeholder'>
          No checkpoints yet
        </p>
      ) : (
        <div className={cn(card, 'overflow-x-auto')}>
          <div className='min-w-[760px]'>
            <div className={cn(COLS, 'px-4 py-3 text-[12px] font-medium text-text-secondary')}>
              <span>Name</span>
              <span>Time ({tz})</span>
              <span>For</span>
              <span>Check-ins</span>
              <span>Status</span>
              <span />
            </div>
            {rows.map((cp) => {
              const { targets, checkedIn, chip } = checkpointSummary(cp, members, checkins, now);
              const checkinCount = checkins.filter((c) => c.checkpointId === cp.id).length;
              return (
                <div
                  key={cp.id}
                  role='button'
                  tabIndex={0}
                  onClick={() => onOpen(cp)}
                  onKeyDown={(e) =>
                    (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen(cp))
                  }
                  className={cn(
                    COLS,
                    'min-h-14 cursor-pointer border-t border-green-deep03/[0.08] px-4 py-2 text-[14px] text-text-primary hover:bg-grayscale-gray01',
                  )}
                >
                  <span className='truncate font-medium'>{cp.name}</span>
                  <span className='tabular-nums'>{formatDue(cp.scheduledAt, displayTz)}</span>
                  <span className='truncate text-[13px] text-text-secondary'>{forLabel(cp)}</span>
                  <span className='flex items-center gap-3'>
                    <span className='tabular-nums'>
                      {checkedIn.length}/{targets.length}
                    </span>
                    <span className='flex'>
                      {checkedIn.map((m, i) => (
                        <MemberAvatar
                          key={m.id}
                          member={m}
                          className={i > 0 ? '-ml-1' : undefined}
                        />
                      ))}
                    </span>
                  </span>
                  <span>
                    <StatusChip chip={chip} />
                  </span>
                  <span className='flex justify-end'>
                    {canManage && cp.type === 'general' && (
                      <RowMenu
                        cp={cp}
                        checkinCount={checkinCount}
                        onEdit={() => onEdit(cp)}
                        onDelete={() => onDelete(cp)}
                      />
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

/** 오른쪽에서 열리는 Sheet — 체크포인트 하나의 팀원별 체크인 */
const CheckinSheet = ({ cp, open, onClose }) => {
  const { data, now, displayTz, memberById } = useWorkboardCtx();
  const tz = tzShortName(displayTz, now);
  const checkins = data.checkins ?? [];
  if (!cp) return null;
  const { targets, checkedIn } = checkpointSummary(cp, [...memberById.values()], checkins, now);

  return (
    <SidePanel open={open} onClose={onClose} label={`${cp.name} check-ins`} width='max-w-[440px]'>
      <header className='flex items-start justify-between gap-3 border-b border-green-deep03/[0.08] px-6 py-5'>
        <div className='flex min-w-0 flex-col gap-0.5'>
          <h2 className='text-[20px] font-medium leading-[130%] text-text-primary'>{cp.name}</h2>
          <span className='text-[13px] text-text-secondary'>
            {formatDue(cp.scheduledAt, displayTz)} {tz} · {forLabel(cp)} · {checkedIn.length}/
            {targets.length} checked in
          </span>
        </div>
        <div className='shrink-0'>
          <CloseButton onClick={onClose} />
        </div>
      </header>
      <ul className='min-h-0 flex-1 divide-y divide-green-deep03/[0.08] overflow-y-auto'>
        {targets.map((m) => {
          const st = checkinStatus(cp, m, checkins, now);
          const ci = checkins.find((c) => c.checkpointId === cp.id && c.userId === m.id);
          return (
            <li key={m.id} className='flex gap-3 px-6 py-4'>
              <MemberAvatar member={m} className='mt-0.5' />
              <div className='flex min-w-0 flex-1 flex-col gap-1'>
                <div className='flex flex-wrap items-center gap-2 text-[13px]'>
                  <span className='text-[14px] font-medium text-text-primary'>{m.name}</span>
                  <span className='text-text-secondary'>{JOB_LABEL[m.job]}</span>
                  <span className='ml-auto flex items-center gap-2'>
                    {st === 'on_time' && isBackFromOffline(cp, ci) && (
                      <span className='text-[12px] text-text-secondary'>Back from offline</span>
                    )}
                    <StatusChip chip={CHECKIN_STATUS[st]} />
                  </span>
                </div>
                {ci ? (
                  <>
                    <span className='text-[12px] text-text-secondary'>
                      Checked in {formatDue(ci.checkedInAt, displayTz)} {tz}
                    </span>
                    <p className='text-[14px] leading-[150%] text-text-primary'>
                      <Linkify text={ci.note} />
                    </p>
                  </>
                ) : (
                  <span className='text-[12px] text-text-secondary'>
                    {st === 'missed' ? 'No check-in yet' : 'Not checked in yet'}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </SidePanel>
  );
};

/** General 체크포인트 지우기 확인 — 아무도 체크인하지 않았을 때만 (W6_DeleteCheckpointDialog) */
const DeleteCheckpointDialog = ({ cp, onClose }) => {
  const { dispatch, now, displayTz } = useWorkboardCtx();
  const tz = tzShortName(displayTz, now);
  return (
    <Modal
      open
      title={`Delete ${cp.name}?`}
      subtitle={`General · ${formatDue(cp.scheduledAt, displayTz)} ${tz} · For ${jobsText(cp.targetJobs ?? [])}`}
      onClose={onClose}
      footer={
        <>
          <ModalButton onClick={onClose}>Cancel</ModalButton>
          <ModalButton
            primary
            onClick={() => {
              dispatch({ type: 'deleteCheckpoint', checkpointId: cp.id, at: now.toISOString() });
              onClose();
            }}
          >
            Delete
          </ModalButton>
        </>
      }
    >
      <div className='flex flex-col gap-1'>
        <span className='text-[13px] font-medium text-text-primary'>Why you can delete it</span>
        <span className='text-[14px] text-text-secondary'>No one has checked in yet.</span>
      </div>
    </Modal>
  );
};

const Team = ({ onOpen, onEdit, onAdd }) => {
  const { data, now, viewer, readOnly } = useWorkboardCtx();
  const leader = isLeader(viewer) && !readOnly && data.project.status !== 'ended';
  const all = [...(data.checkpoints ?? [])].sort(byTime);
  const [deleting, setDeleting] = useState(null);
  // 시작 전 — Add checkpoint 는 프로젝트 시작부터
  const started = now >= new Date(data.project.startAt) && data.project.status !== 'draft';

  return (
    <div className='flex flex-col gap-10'>
      <TeamTable
        title='Requested'
        desc={
          leader
            ? 'Set by ops for the whole team. Read-only for the leader.'
            : 'Set by ops for the whole team.'
        }
        rows={all.filter((c) => c.type === 'requested')}
        onOpen={onOpen}
      />
      <TeamTable
        title='General'
        desc={
          leader
            ? 'Added by you for chosen jobs. Edit anytime; delete only with no check-ins.'
            : 'Added by the leader for chosen jobs.'
        }
        rows={all.filter((c) => c.type === 'general')}
        canManage={leader}
        onOpen={onOpen}
        onEdit={onEdit}
        onDelete={setDeleting}
        action={
          leader && (
            <TdButton
              onClick={onAdd}
              disabled={!started}
              title={
                started
                  ? undefined
                  : `Available from ${formatClock(new Date(data.project.startAt), 'Asia/Seoul')} KST`
              }
            >
              Add checkpoint
            </TdButton>
          )
        }
      />
      {deleting && <DeleteCheckpointDialog cp={deleting} onClose={() => setDeleting(null)} />}
    </div>
  );
};

/**
 * Checkpoints (MVP §8-10) — My check-ins(기본) / Team.
 * 팀원은 Team 탭을 보기만 한다. 리더는 General 을 만들고 고치고 지운다. Requested 는 운영진 것이라 읽기 전용.
 */
const TABS = [
  ['mine', 'My check-ins'],
  ['team', 'Team'],
];

const TwoDaysCheckpoints = () => {
  const { data, now, viewer, closed, closedTip } = useWorkboardCtx();
  // 이 페이지를 열면 지금 Due now 인 체크포인트는 본 것으로 — 사이드바 배지가 지워진다 (Due now · Seen)
  const dueIds = (data.checkpoints ?? [])
    .filter((c) => isDueNow(c, viewer, data.checkins ?? [], now))
    .map((c) => c.id);
  const dueKey = dueIds.join(',');
  useEffect(() => {
    if (dueIds.length) markCheckpointsSeen(data, dueIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dueKey]);
  // 스태프는 체크인하지 않는다 — 탭은 그대로 두고 Team(읽기 전용)부터 보여 준다
  const [tab, setTab] = useState(() => (viewer?.staff ? 'team' : 'mine'));
  const lit = useInboxFocus();
  useEffect(() => {
    if (lit?.checkpointId && !viewer?.staff) setTab('mine');
  }, [lit, viewer?.staff]);
  const [sheet, setSheet] = useState(null);
  const [shownSheet, setShownSheet] = useState(null);
  const [editing, setEditing] = useState(undefined); // undefined = 닫힘, null = 새로 만들기
  const openSheet = (cp) => {
    setShownSheet(cp);
    setSheet(cp);
  };

  return (
    <div className='flex max-w-[960px] flex-col gap-6'>
      <h1 className='text-[28px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary'>
        Checkpoints
      </h1>
      {closed && !viewer?.staff && (
        <p className='rounded-xl bg-grayscale-gray01 px-4 py-3 text-[13px] leading-[150%] text-text-secondary'>
          {closedTip}
        </p>
      )}

      <div
        role='tablist'
        aria-label='Checkpoints'
        className='flex w-fit gap-0.5 rounded-xl bg-grayscale-gray02/70 p-1'
      >
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type='button'
            role='tab'
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={cn(
              'h-8 rounded-lg px-3.5 text-[14px] transition-colors',
              tab === key
                ? 'bg-grayscale-gray00 font-medium text-text-primary shadow-[0_1px_3px_rgba(0,0,0,0.12)]'
                : 'text-text-secondary hover:text-text-primary',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'mine' ? (
        <MyCheckins litId={lit?.checkpointId ?? null} />
      ) : (
        <Team onOpen={openSheet} onEdit={setEditing} onAdd={() => setEditing(null)} />
      )}
      <CheckinSheet cp={shownSheet} open={!!sheet} onClose={() => setSheet(null)} />
      {editing !== undefined && (
        <GeneralCheckpointModal checkpoint={editing} onClose={() => setEditing(undefined)} />
      )}
    </div>
  );
};

export default TwoDaysCheckpoints;
